// Per-row receipt status chip (slice 4g). Renders the
// all-posted / partial / not-OCR'd / dismissed badge used on the
// Receipts tab list. Pure presentational -- consumes the
// pre-classified `attention` item from the receipts-attention hook;
// when that hasn't loaded yet, falls back to a softer "extracted"
// chip so the UI doesn't flash from emerald to amber as the data
// resolves.
import type {
  MfgInventoryReceipt,
  MfgReceiptAttentionItem,
} from "@/lib/manufacturing/types";

type Props = {
  receipt: MfgInventoryReceipt;
  attention: MfgReceiptAttentionItem | null;
};

export function ManufacturingReceiptStatusChip({ receipt, attention }: Props) {
  if (!attention) {
    return receipt.extracted_at ? (
      <span
        title={`OCR extracted ${new Date(receipt.extracted_at).toLocaleString()}`}
        className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/35 dark:text-emerald-200"
      >
        extracted
      </span>
    ) : null;
  }
  switch (attention.status) {
    case "all_posted":
      return (
        <span
          title="All extracted lines from this receipt have a posted cost entry."
          className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/35 dark:text-emerald-200"
        >
          all posted
        </span>
      );
    case "partial_posted":
      return (
        <span
          title="Some lines posted; others still need an operator to review and post."
          className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-900 dark:bg-amber-950/35 dark:text-amber-200"
        >
          {attention.posted_line_count} of {attention.extracted_line_count} posted
        </span>
      );
    case "none_posted":
      return (
        <span
          title="OCR extracted lines but none have been posted yet. Open Review."
          className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-900 dark:bg-amber-950/35 dark:text-amber-200"
        >
          0 of {attention.extracted_line_count} posted
        </span>
      );
    case "not_extracted":
      return (
        <span
          title="Receipt uploaded but OCR hasn't run. Open Review to extract."
          className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-800 dark:border-blue-900 dark:bg-blue-950/35 dark:text-blue-200"
        >
          not OCR'd
        </span>
      );
    case "dismissed":
      return (
        <span
          title={`Alert silenced ${attention.dismissed_at ? new Date(attention.dismissed_at).toLocaleString() : ""}. Re-open Review to re-surface.`}
          className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400"
        >
          dismissed
        </span>
      );
  }
}
